/**
 * The card at the end of a run, in the run's own message in the thread: what it
 * found, in four lines — held up, didn't survive, still open, next — each a press
 * to where that line is read in full. The narration's wrap-up, when there is one,
 * sits beneath the message exactly as before; this is the part that needs no key.
 *
 * WHY HERE AND NOT ON THE STRIP. The space around the input carries at most one
 * status line and one "next" line, and after a run the strip already holds both:
 * the stop head, and the rest line with Continue or Run (D179). Four more lines
 * there would be the done bar this app took down. The run's message in the thread
 * is where the run is read; the card is its ending. The strip's head carries this
 * card's counts and nothing else, and its press — "What it found" — opens the
 * thread on this card (`openRunFound`, D335), so the ending on screen points at
 * the ending in the thread rather than past it to the ledger.
 *
 * The lines are `core/runFound.ts`'s — counts and names the record says, in the
 * table's words; the past-tense two off the run's own record, "still open"
 * against the graph as it stands — and every press here is free: a tab and a fold
 * (`openReportSection`), the run's account, or a claim selected. None of them
 * calls a model (invariant 9), and none of them is offered as a press that would.
 *
 * "Next" is read live, for the newest run only: what the run's own policy would
 * take first on the graph as it now stands (`chooseMove`, the call the loop
 * makes, with a fresh run's state — which is what Run and Continue both start
 * from), and after a Stop — or an API that stopped answering (D337) — the moves
 * the run left (`continuationOf`). An older run's next is a question the graph
 * has moved past, so its card is three lines; and an agenda run's next is the
 * queue's, which the Lab shows. A run that ran a conjecture pass has one line
 * more, before Next: what it proposed and how many of those no prior-art search
 * has read yet, opening the conjecture ledger (D289).
 */

import { useEffect, useMemo, useRef } from 'react'
import type { Run } from '../core/types'
import { useStore } from '../core/store'
import {
  FOUND_SECTION_TITLE,
  foundWord,
  runFound,
  type FoundNext,
  type FoundPart,
  type FoundRow,
} from '../core/runFound'
import { chooseMove, freshRunState } from '../engine/runPolicy'
import { continuationOf } from '../engine/run'
import { openReportSection } from './reportLink'
import { Icon } from './components/Icon'

/**
 * The strip's "What it found": the thread, opened on this run's card.
 *
 * `openReportSection`'s shape, for the same reason — nothing but the card reads
 * it, and the ask MOUNTS the surface it arms. The card for that run takes it on
 * the way in (or now, when it is already on screen), brings itself into view and
 * takes focus on its first line, since the press that asked stays on the strip
 * and a keyboard reader would otherwise start from there again. An ask carries
 * its exploration and a moment's lifetime: one made on another graph, or one no
 * card took in time (a thread that never opened it), is dropped rather than
 * scrolling the thread the next time that card happens to mount. Free: a pane
 * and a scroll. Never persisted.
 */
let foundAsk: { session: string; run: string; at: number } | null = null
const foundTakers = new Set<() => void>()

export function openRunFound(runId: string): void {
  const s = useStore.getState()
  foundAsk = { session: s.session.id, run: runId, at: Date.now() }
  s.setRail(true)
  s.setRightTab('node')
  for (const take of foundTakers) take()
}

/** Where a line goes, in the words its hover says. */
function goesTo(row: FoundRow): string {
  switch (row.to.kind) {
    case 'section':
      return `Opens “${FOUND_SECTION_TITLE[row.to.id]}” in the Overview.`
    case 'account':
      return 'Opens the run’s account: every move, and the open ends it left.'
    case 'node':
      return 'Selects the claim. Nothing runs until you press something on it.'
  }
}

function Part({ part }: { part: FoundPart }) {
  return (
    <>
      {part.text}
      {part.names?.length ? (
        <>
          {part.text ? (part.into ? ' ' : ': ') : ''}
          {part.names.map((n, i) => (
            <span key={i}>
              {i > 0 ? ', ' : ''}
              <span className="feed-found-name">“{n}”</span>
            </span>
          ))}
          {part.more ? ` and ${part.more} more` : ''}
        </>
      ) : null}
    </>
  )
}

export function RunFound({
  run,
  onPivot,
  onAccount,
}: {
  run: Run
  onPivot: (id: string) => void
  onAccount: () => void
}) {
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const newest = session.runs.at(-1)?.id === run.id

  // The policy over the whole graph: once per graph change, not per render.
  const next = useMemo<FoundNext | null>(() => {
    if (!newest || run.agenda) return null
    // Whatever ending leaves a rest is `continuationOf`'s to say — a Stop, or an
    // API that stopped answering (D337) — so the card and Continue cannot differ.
    const cont = continuationOf(run)
    const move = chooseMove(session, freshRunState(cont?.moves ?? run.budget.moves))
    return {
      pick: move ? { nodeId: move.nodeId, label: move.label, why: move.why } : null,
      left: cont?.moves ?? null,
    }
    // `rev` because a patch can land on a node without a new session object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newest, run, session, rev])

  // "Still open" reads the graph too, so it follows the same `rev`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => runFound(run, session, next), [run, session, next, rev])

  // The strip's ask, taken by this run's card and no other (`openRunFound`).
  const list = useRef<HTMLUListElement | null>(null)
  const sessionId = session.id
  useEffect(() => {
    const take = () => {
      const a = foundAsk
      if (!a || a.run !== run.id) return
      foundAsk = null
      if (a.session !== sessionId || Date.now() - a.at > 10_000) return
      const el = list.current
      if (!el) return
      el.scrollIntoView?.({ block: 'nearest' })
      el.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true })
    }
    take()
    foundTakers.add(take)
    return () => {
      foundTakers.delete(take)
    }
  }, [run.id, sessionId])

  // A run stopped before its first move found nothing its head does not say —
  // the narrator's rule for a wrap-up (`narratableRun`), kept for the card.
  if (!run.moves.length) return null

  const go = (row: FoundRow) => {
    const to = row.to
    if (to.kind === 'section') openReportSection(to.id)
    else if (to.kind === 'account') onAccount()
    else onPivot(to.id)
  }

  return (
    <ul ref={list} className="feed-found" aria-label="What the run found">
      {rows.map((row) => (
        <li key={row.line}>
          <button className="feed-found-row" title={goesTo(row)} onClick={() => go(row)}>
            <span className="feed-found-word">{foundWord(row.line)}</span>
            <b className="num feed-found-count">{row.count ?? ''}</b>
            <span className="feed-found-what">
              {row.parts.map((p, i) => (
                <span key={i}>
                  {i > 0 ? ' · ' : ''}
                  <Part part={p} />
                </span>
              ))}
              {/* The policy's reason as its own sentence: a claim named as next
                  without why is the run going somewhere for no stated cause. */}
              {row.why && (
                <span className="feed-found-why">
                  {row.why.charAt(0).toUpperCase()}
                  {row.why.slice(1)}.
                </span>
              )}
            </span>
            <Icon name="chevron" size={9} className="feed-found-go" />
          </button>
        </li>
      ))}
    </ul>
  )
}
